import React, { useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, Polyline, useJsApiLoader } from "@react-google-maps/api";
import { initSocket } from "../initSocket";
import { Box, HStack, Text, Badge } from "@chakra-ui/react";

type LatLng = { lat: number; lng: number; };

const CONTAINER_STYLE = { width: "100%", height: "70vh" };

// Linear interpolation
function lerp(a: number, b: number, t: number) { return a + (b - a) * t; }
function interpolate(p1: LatLng, p2: LatLng, t: number): LatLng {
    return { lat: lerp(p1.lat, p2.lat, t), lng: lerp(p1.lng, p2.lng, t) };
}
// Bearing in degrees (for rotating car icon)
function bearing(from: LatLng, to: LatLng) {
    const toRad = (d: number) => d * Math.PI / 180;
    const toDeg = (r: number) => r * 180 / Math.PI;
    const φ1 = toRad(from.lat), φ2 = toRad(to.lat);
    const Δλ = toRad(to.lng - from.lng);
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);
    return (toDeg(θ) + 360) % 360;
}

export default function CustomerLiveTracker({
    tripId,
    initialCenter,
}: {
    tripId: string;
    initialCenter?: LatLng;
}) {
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string,
        id: "live-tracker",
    });

    const mapRef = useRef<google.maps.Map | null>(null);
    const [path, setPath] = useState<LatLng[]>([]);
    const [displayPos, setDisplayPos] = useState<LatLng | null>(initialCenter ?? null);
    const [speedKph, setSpeedKph] = useState<number>(0);
    const [status, setStatus] = useState<string>("Waiting for driver…");

    // animation state
    const lastServerPos = useRef<LatLng | null>(null);
    const nextServerPos = useRef<LatLng | null>(null);
    const animStart = useRef<number>(0);
    const animDuration = useRef<number>(1500); // ms

    // connect socket and receive location
    useEffect(() => {
        let mounted = true;
        (async () => {
            const socket = await initSocket();
            socket.emit("joinTripRoom", { tripId });

            socket.on("locationUpdate", ({ lat, lng, speed }: { lat: number; lng: number; speed?: number; }) => {
                if (!mounted) return;
                const pos = { lat, lng };
                lastServerPos.current = displayPos ?? pos;
                nextServerPos.current = pos;
                animStart.current = performance.now();
                setSpeedKph(Math.max(0, Math.round(speed ?? 0)));
                setPath((prev) =>
                    prev.length && prev[prev.length - 1].lat === lat && prev[prev.length - 1].lng === lng
                        ? prev
                        : [...prev, pos]
                );
                setStatus("Driver en route");
            });

            socket.on("statusUpdate", ({ status: s }: { status: string; }) => {
                if (mounted) setStatus(s);
            });

            socket.on("disconnect", () => {
                if (mounted) setStatus("Driver disconnected");
            });
        })();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tripId]);

    // animate marker smoothly between server updates
    useEffect(() => {
        let raf = 0;
        const step = (now: number) => {
            const a = lastServerPos.current;
            const b = nextServerPos.current;
            if (!a || !b) {
                raf = requestAnimationFrame(step);
                return;
            }
            const t = Math.min(1, (now - animStart.current) / animDuration.current);
            const p = interpolate(a, b, t);
            setDisplayPos(p);

            // auto-pan slightly when we reach the next point
            if (mapRef.current && t === 1) {
                const bounds = new google.maps.LatLngBounds();
                [...path.slice(-20), p].forEach((pt) => bounds.extend(pt as any));
                mapRef.current.fitBounds(bounds, 80);
            }
            raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
        return () => cancelAnimationFrame(raf);
    }, [path.length]);

    const heading = useMemo(() => {
        if (path.length < 2) return 0;
        return bearing(path[path.length - 2], path[path.length - 1]);
    }, [path]);

    if (!isLoaded) return <Box p={4}>Loading map…</Box>;

    return (
        <Box>
            <HStack spacing={4} mb={2}>
                <Badge colorScheme="teal">{status}</Badge>
                <Text fontSize="sm">Speed: {speedKph} km/h</Text>
            </HStack>

            <GoogleMap
                mapContainerStyle={CONTAINER_STYLE}
                // ✅ return void here, don't return the map
                onLoad={(m: google.maps.Map) => {
                    mapRef.current = m;
                }}
                center={displayPos ?? initialCenter ?? { lat: -15.7861, lng: 35.0056 }}
                zoom={15}
                options={{
                    streetViewControl: false,
                    mapTypeControl: false,
                    fullscreenControl: false,
                    clickableIcons: false,
                }}
            >
                {path.length > 1 && (
                    <Polyline
                        path={path}
                        options={{
                            strokeOpacity: 0.9,
                            strokeWeight: 5,
                        }}
                    />
                )}

                {displayPos && (
                    <Marker
                        position={displayPos}
                        icon={{
                            url:
                                "data:image/svg+xml;charset=UTF-8," +
                                encodeURIComponent(
                                    `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="-12 -12 72 72">
                    <g transform="rotate(${heading},24,24)">
                      <path d="M24 2 L36 26 L24 22 L12 26 Z" fill="#4285F4"/>
                      <circle cx="24" cy="24" r="4" fill="#fff"/>
                    </g>
                  </svg>`
                                ),
                            // ✅ anchor must be a google.maps.Point
                            anchor: new google.maps.Point(24, 24),
                            scaledSize: new google.maps.Size(48, 48),
                        }}
                    />
                )}
            </GoogleMap>
        </Box>
    );
}
